import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, Clock, Package, Download, Lock, Upload, MessageSquare, FileText, Send, LogOut, Mail, Github, Rocket, ExternalLink, Loader2 } from "lucide-react";
import { BackButton } from "@/components/layout/BackButton";
import factoryBlueprint from "@assets/generated_images/futuristic_digital_blueprint_of_an_ai_software_factory.png";
import { ClientPortalSEO } from "@/components/SEO";
import { MainContent } from "@/components/MainContent";

interface ClientUser {
  id: number;
  email: string;
  leadId?: number;
}

interface Order {
  id: number;
  status: string;
  paidStatus: string;
  spec: string;
  quoteCents: number;
  quoteBreakdown: Array<{ key: string; label: string; cents: number }>;
  createdAt: string;
}

interface OrderMessage {
  id: number;
  senderType: string;
  content: string;
  createdAt: string;
}

interface OrderFile {
  id: number;
  originalName: string;
  sizeBytes: number;
  createdAt: string;
}

interface Artifact {
  id: number;
  filename: string;
  type: string;
  accessToken: string;
}

interface Integration {
  id: number;
  provider: string;
  providerUsername: string;
  isActive: boolean;
  createdAt: string;
}

interface DeploymentInfo {
  id: number;
  status: string;
  provider: string | null;
  liveUrl: string | null;
  repoUrl: string | null;
  currentStage: string | null;
  version: number;
  createdAt: string;
}

interface DeployStage {
  stageType: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

interface OrderDetails {
  order: Order;
  job?: { id: number; status: string; currentStep?: string };
  versions: any[];
  currentVersion?: any;
  artifacts: Artifact[];
  files: OrderFile[];
  messages: OrderMessage[];
}

// Generate consistent fake team members based on order ID
function getProjectTeam(orderId: number) {
  const teams = [
    { lead: "Sarah Chen", architect: "Marcus Williams", qa: "Priya Patel" },
    { lead: "Jennifer Lopez", architect: "David Kim", qa: "Ahmed Hassan" },
    { lead: "Emily Watson", architect: "James Rodriguez", qa: "Lisa Chen" },
    { lead: "Maria Garcia", architect: "Robert Taylor", qa: "Yuki Tanaka" },
    { lead: "Amanda Foster", architect: "Michael Chang", qa: "Sofia Andersson" },
  ];
  return teams[orderId % teams.length];
}

export default function ClientPortal() {
  const [, navigate] = useLocation();
  const [authMode, setAuthMode] = useState<"login" | "register" | "magic">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  
  const [token, setToken] = useState<string | null>(localStorage.getItem("client_token"));
  const [user, setUser] = useState<ClientUser | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  
  const [newMessage, setNewMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [deploymentData, setDeploymentData] = useState<{ deployment: DeploymentInfo | null; stages: DeployStage[] } | null>(null);
  const [connectingGithub, setConnectingGithub] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      fetchOrders();
      fetchIntegrations();
    }
  }, [user]);

  useEffect(() => {
    if (selectedOrderId) {
      fetchOrderDetails(selectedOrderId);
    }
  }, [selectedOrderId]);

  const fetchUser = async () => {
    try {
      const res = await fetch("/api/client/me", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setUser(await res.json());
      } else {
        handleLogout();
      }
    } catch {
      handleLogout();
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch("/api/client/orders", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders);
        if (data.orders.length > 0 && !selectedOrderId) {
          setSelectedOrderId(data.orders[0].id);
        }
      }
    } catch (e) {
      console.error("Failed to fetch orders:", e);
    }
  };

  const fetchOrderDetails = async (orderId: number) => {
    try {
      const res = await fetch(`/api/client/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setOrderDetails(await res.json());
      }
      fetchDeploymentStatus(orderId);
    } catch (e) {
      console.error("Failed to fetch order details:", e);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const res = await fetch("/api/client/integrations", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data.integrations || []);
      }
    } catch (e) {
      console.error("Failed to fetch integrations:", e);
    }
  };

  const fetchDeploymentStatus = async (orderId: number) => {
    try {
      const res = await fetch(`/api/client/deployments/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDeploymentData(data);
      }
    } catch (e) {
      console.error("Failed to fetch deployment status:", e);
    }
  };

  const connectGithub = async () => {
    setConnectingGithub(true);
    try {
      const res = await fetch("/api/integrations/github/auth", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.authUrl;
      } else {
        const err = await res.json();
        setError(err.error || "Failed to connect GitHub");
        setConnectingGithub(false);
      }
    } catch (e) {
      setError("Failed to connect GitHub");
      setConnectingGithub(false);
    }
  };

  const disconnectIntegration = async (id: number) => {
    try {
      const res = await fetch(`/api/client/integrations/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchIntegrations();
      }
    } catch (e) {
      console.error("Failed to disconnect integration:", e);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const endpoint = authMode === "register" ? "/api/client/register" : "/api/client/login";
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("client_token", data.token);
        setToken(data.token);
        setUser(data.user);
      } else {
        setError(data.error || "Authentication failed");
      }
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const res = await fetch("/api/client/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      if (res.ok) {
        setMagicSent(true);
      } else {
        const data = await res.json();
        setError(data.error || "Failed to send magic link");
      }
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("client_token");
    setToken(null);
    setUser(null);
    setOrders([]);
    setSelectedOrderId(null);
    setOrderDetails(null);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedOrderId) return;
    
    try {
      const res = await fetch(`/api/client/orders/${selectedOrderId}/messages`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage })
      });
      
      if (res.ok) {
        setNewMessage("");
        fetchOrderDetails(selectedOrderId);
      }
    } catch (e) {
      console.error("Failed to send message:", e);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOrderId) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await fetch(`/api/client/orders/${selectedOrderId}/files`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        fetchOrderDetails(selectedOrderId);
      }
    } catch (e) {
      console.error("Failed to upload file:", e);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!token) {
    if (magicSent) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 z-0">
            <img src={factoryBlueprint} className="w-full h-full object-cover" alt="Blueprint" />
          </div>
          <Card className="w-full max-w-md border-primary/20 bg-card/80 backdrop-blur-xl relative z-10 shadow-2xl">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/50">
                <Mail className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-2xl">Check Your Email</CardTitle>
                <CardDescription>We sent a magic link to {email}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-4">Click the link in your email to sign in. The link expires in 15 minutes.</p>
              <Button variant="outline" onClick={() => setMagicSent(false)}>Back to Login</Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 z-0">
          <img src={factoryBlueprint} className="w-full h-full object-cover" alt="Blueprint" />
        </div>
        
        <Card className="w-full max-w-md border-primary/20 bg-card/80 backdrop-blur-xl relative z-10 shadow-2xl" data-testid="login-card">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center border border-primary/50">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Client Portal</CardTitle>
              <CardDescription>
                {authMode === "login" ? "Sign in to view your orders" : 
                 authMode === "register" ? "Create your account" : 
                 "Sign in with email link"}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={authMode === "magic" ? handleMagicLink : handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email Address</label>
                <Input 
                  type="email" 
                  placeholder="you@company.com" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-background/50"
                  required
                  data-testid="input-email"
                />
              </div>
              {authMode !== "magic" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <Input 
                    type="password" 
                    placeholder="••••••••" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-background/50"
                    data-testid="input-password"
                  />
                </div>
              )}
              {error && <p className="text-red-500 text-sm" data-testid="text-error">{error}</p>}
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="button-submit">
                {loading ? "Please wait..." : 
                 authMode === "login" ? "Sign In" :
                 authMode === "register" ? "Create Account" :
                 "Send Magic Link"}
              </Button>
            </form>
            
            <div className="mt-6 text-center space-y-2">
              {authMode === "login" && (
                <>
                  <Button variant="link" onClick={() => { setAuthMode("magic"); setError(""); }} className="text-sm">
                    Sign in with email link instead
                  </Button>
                  <div className="text-muted-foreground text-sm">
                    Don't have an account? <Button variant="link" onClick={() => { setAuthMode("register"); setError(""); }} className="p-0">Register</Button>
                  </div>
                </>
              )}
              {authMode === "register" && (
                <div className="text-muted-foreground text-sm">
                  Already have an account? <Button variant="link" onClick={() => { setAuthMode("login"); setError(""); }} className="p-0">Sign In</Button>
                </div>
              )}
              {authMode === "magic" && (
                <Button variant="link" onClick={() => { setAuthMode("login"); setError(""); }} className="text-sm">
                  Use password instead
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const steps = [
    { id: "locked", label: "Order Confirmed", icon: Lock },
    { id: "paid", label: "Payment Received", icon: CheckCircle2 },
    { id: "building", label: "In Preparation", icon: Clock },
    { id: "ready", label: "Ready for Review", icon: Package },
    { id: "delivered", label: "Delivered", icon: Download },
  ];

  const currentOrder = orderDetails?.order;
  const currentStepIndex = currentOrder ? steps.findIndex(s => s.id === currentOrder.status) : 0;
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  return (
    <>
      <ClientPortalSEO />
      <div className="min-h-screen bg-background pb-12">
        <header className="bg-card/50 border-b border-white/5 backdrop-blur-md sticky top-0 z-40">
          <div className="container mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BackButton fallback="/" />
              <div className="w-8 h-8 rounded bg-primary flex items-center justify-center font-bold text-white">
                AI
              </div>
              <span className="font-bold tracking-tight">Client Portal</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span data-testid="text-user-email">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="button-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        <MainContent className="container mx-auto px-6 py-8 space-y-8">
        {orders.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h2 className="text-xl font-bold mb-2">No Orders Yet</h2>
              <p className="text-muted-foreground mb-4">You don't have any orders associated with your account.</p>
              <Button onClick={() => navigate("/build")}>Start a New Build</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {orders.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {orders.map((order) => (
                  <Button
                    key={order.id}
                    variant={selectedOrderId === order.id ? "default" : "outline"}
                    onClick={() => setSelectedOrderId(order.id)}
                    className="shrink-0"
                    data-testid={`button-order-${order.id}`}
                  >
                    Order #{order.id}
                  </Button>
                ))}
              </div>
            )}

            {currentOrder && (
              <>
                <section className="relative rounded-2xl overflow-hidden bg-card border border-white/5 shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent" />
                  <div className="relative p-8 md:p-12">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                      <div>
                        <Badge variant="outline" className="mb-2 bg-primary/10 text-primary border-primary/20">
                          Order #{currentOrder.id}
                        </Badge>
                        <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="text-order-status">
                          {currentOrder.status === 'building' ? 'Preparing Your Solution' : 'Order Status'}
                        </h1>
                        <p className="text-muted-foreground">Created: {new Date(currentOrder.createdAt).toLocaleDateString()}</p>
                      </div>
                      {currentOrder.status === 'building' && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 text-yellow-500 rounded-full border border-yellow-500/20 animate-pulse">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">In Progress</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-8">
                      <Progress value={progress} className="h-2 bg-white/5" />
                      <div className="grid grid-cols-5 gap-2">
                        {steps.map((step, idx) => {
                          const isActive = idx <= currentStepIndex;
                          const isCurrent = idx === currentStepIndex;
                          return (
                            <div key={step.id} className={`flex flex-col items-center gap-3 ${isActive ? 'opacity-100' : 'opacity-40'}`}>
                              <div className={`
                                w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all
                                ${isActive ? 'bg-primary border-primary text-white' : 'bg-transparent border-white/20 text-muted-foreground'}
                                ${isCurrent ? 'ring-4 ring-primary/20' : ''}
                              `}>
                                <step.icon className="w-5 h-5" />
                              </div>
                              <span className="text-xs md:text-sm font-medium text-center hidden md:block">{step.label}</span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Project Team Assignment */}
                      <div className="bg-muted/30 rounded-lg p-4 border border-white/5">
                        <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Your Dedicated Project Team</h3>
                        <div className="grid grid-cols-3 gap-4">
                          {(() => {
                            const team = getProjectTeam(currentOrder.id);
                            return (
                              <>
                                <div className="text-center">
                                  <div className="w-10 h-10 rounded-full bg-primary/20 mx-auto mb-2 flex items-center justify-center text-sm font-bold text-primary">
                                    {team.lead.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <p className="text-xs font-medium">{team.lead}</p>
                                  <p className="text-xs text-muted-foreground">Project Lead</p>
                                </div>
                                <div className="text-center">
                                  <div className="w-10 h-10 rounded-full bg-blue-500/20 mx-auto mb-2 flex items-center justify-center text-sm font-bold text-blue-500">
                                    {team.architect.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <p className="text-xs font-medium">{team.architect}</p>
                                  <p className="text-xs text-muted-foreground">Technical Architect</p>
                                </div>
                                <div className="text-center">
                                  <div className="w-10 h-10 rounded-full bg-green-500/20 mx-auto mb-2 flex items-center justify-center text-sm font-bold text-green-500">
                                    {team.qa.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <p className="text-xs font-medium">{team.qa}</p>
                                  <p className="text-xs text-muted-foreground">Quality Specialist</p>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <Tabs defaultValue="details" className="space-y-6">
                  <TabsList>
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="messages">Messages ({orderDetails?.messages?.length || 0})</TabsTrigger>
                    <TabsTrigger value="files">Files ({orderDetails?.files?.length || 0})</TabsTrigger>
                    <TabsTrigger value="deliverables">Deliverables</TabsTrigger>
                    <TabsTrigger value="deployment" data-testid="tab-deployment">
                      <Rocket className="w-4 h-4 mr-1 inline" />
                      Deployment
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="details">
                    <div className="grid md:grid-cols-3 gap-8">
                      <div className="md:col-span-2">
                        <Card className="bg-card/50 border-white/5">
                          <CardHeader>
                            <CardTitle>System Specifications</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="bg-background/50 p-4 rounded-lg border border-white/5 font-mono text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
                              {currentOrder.spec}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                      <div>
                        <Card className="bg-card/50 border-white/5 h-full">
                          <CardHeader>
                            <CardTitle>Order Summary</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {currentOrder.quoteBreakdown?.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{item.label}</span>
                                <span>${(item.cents / 100).toLocaleString()}</span>
                              </div>
                            ))}
                            <div className="h-px bg-white/10 my-2" />
                            <div className="flex justify-between items-center">
                              <span className="font-bold">Total</span>
                              <span className="text-xl font-bold text-primary">
                                ${(currentOrder.quoteCents / 100).toLocaleString()}
                              </span>
                            </div>
                            <Badge variant={currentOrder.paidStatus === "paid" ? "default" : "secondary"}>
                              {currentOrder.paidStatus === "paid" ? "Paid" : "Payment Pending"}
                            </Badge>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="messages">
                    <Card className="bg-card/50 border-white/5">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <MessageSquare className="w-5 h-5" />
                          Message Thread
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="max-h-96 overflow-y-auto space-y-4 p-4 bg-background/50 rounded-lg border border-white/5">
                          {orderDetails?.messages?.length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">No messages yet. Start the conversation!</p>
                          ) : (
                            orderDetails?.messages?.map((msg) => (
                              <div key={msg.id} className={`flex ${msg.senderType === 'client' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] rounded-lg p-3 ${
                                  msg.senderType === 'client' 
                                    ? 'bg-primary text-primary-foreground' 
                                    : msg.senderType === 'admin'
                                    ? 'bg-secondary'
                                    : 'bg-muted'
                                }`}>
                                  <p className="text-sm">{msg.content}</p>
                                  <p className="text-xs opacity-70 mt-1">
                                    {msg.senderType === 'admin' && 'Support • '}
                                    {new Date(msg.createdAt).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Textarea
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            className="flex-1"
                            data-testid="input-message"
                          />
                          <Button onClick={sendMessage} disabled={!newMessage.trim()} data-testid="button-send-message">
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="files">
                    <Card className="bg-card/50 border-white/5">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          Uploaded Files
                        </CardTitle>
                        <CardDescription>Attach documents, images, or reference files to your order</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-4">
                            Drag and drop files here, or click to browse
                          </p>
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="file-upload"
                            accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.zip,.txt"
                            disabled={uploading}
                          />
                          <Button asChild variant="outline" disabled={uploading}>
                            <label htmlFor="file-upload" className="cursor-pointer">
                              {uploading ? "Uploading..." : "Choose File"}
                            </label>
                          </Button>
                          <p className="text-xs text-muted-foreground mt-2">
                            Max 10MB. Allowed: PDF, DOCX, PNG, JPG, ZIP, TXT
                          </p>
                        </div>

                        {orderDetails?.files?.length ? (
                          <div className="space-y-2">
                            {orderDetails.files.map((file) => (
                              <div key={file.id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg border border-white/5">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-muted-foreground" />
                                  <div>
                                    <p className="text-sm font-medium">{file.originalName}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {(file.sizeBytes / 1024).toFixed(1)} KB • {new Date(file.createdAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={`/api/files/${file.id}/download`} download>
                                    <Download className="w-4 h-4" />
                                  </a>
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-muted-foreground py-4">No files uploaded yet</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="deliverables">
                    <Card className="bg-card/50 border-white/5">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Package className="w-5 h-5" />
                          Deliverables
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {orderDetails?.artifacts?.length ? (
                          <div className="space-y-4">
                            {orderDetails.currentVersion && (
                              <Badge variant="outline" className="mb-4">
                                Version {orderDetails.currentVersion.version} (Current)
                              </Badge>
                            )}
                            {orderDetails.artifacts.map((artifact) => (
                              <div key={artifact.id} className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <Package className="w-8 h-8 text-green-500" />
                                  <div>
                                    <h4 className="font-bold text-green-500">{artifact.filename}</h4>
                                    <p className="text-sm text-green-400/80">{artifact.type}</p>
                                  </div>
                                </div>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" asChild>
                                  <a href={`/api/artifacts/${artifact.id}/download?token=${artifact.accessToken}`}>
                                    <Download className="w-4 h-4 mr-2" /> Download
                                  </a>
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center p-8 border border-dashed border-white/10 rounded-lg text-muted-foreground">
                            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No deliverables available yet.</p>
                            <p className="text-sm mt-1">Deliverables will appear here once your build is complete.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="deployment">
                    <div className="grid md:grid-cols-2 gap-6">
                      <Card className="bg-card/50 border-white/5">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Github className="w-5 h-5" />
                            Connected Accounts
                          </CardTitle>
                          <CardDescription>
                            Connect your GitHub account to enable automatic code deployment
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {integrations.filter(i => i.isActive).length > 0 ? (
                            <div className="space-y-3">
                              {integrations.filter(i => i.isActive).map((integration) => (
                                <div key={integration.id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg border border-white/5">
                                  <div className="flex items-center gap-3">
                                    <Github className="w-6 h-6" />
                                    <div>
                                      <p className="font-medium">{integration.providerUsername}</p>
                                      <p className="text-xs text-muted-foreground">Connected</p>
                                    </div>
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => disconnectIntegration(integration.id)}
                                    data-testid="button-disconnect-github"
                                  >
                                    Disconnect
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <Github className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                              <p className="text-muted-foreground mb-4">
                                Connect GitHub to deploy your project to a repository
                              </p>
                              <Button 
                                onClick={connectGithub} 
                                disabled={connectingGithub}
                                data-testid="button-connect-github"
                              >
                                {connectingGithub ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Connecting...
                                  </>
                                ) : (
                                  <>
                                    <Github className="w-4 h-4 mr-2" />
                                    Connect GitHub
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-card/50 border-white/5">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Rocket className="w-5 h-5" />
                            Deployment Status
                          </CardTitle>
                          <CardDescription>
                            Track the deployment progress of your project
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {deploymentData?.deployment ? (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <Badge className={`
                                  ${deploymentData.deployment.status === "live" ? "bg-green-500" : ""}
                                  ${deploymentData.deployment.status === "failed" ? "bg-red-500" : ""}
                                  ${["queued", "intake", "building", "deploying"].includes(deploymentData.deployment.status) ? "bg-blue-500" : ""}
                                `}>
                                  {deploymentData.deployment.status === "live" ? "ACTIVE" : 
                                   deploymentData.deployment.status === "failed" ? "ERROR" : "PREPARING"}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  v{deploymentData.deployment.version}
                                </span>
                              </div>

                              {deploymentData.deployment.liveUrl && (
                                <a 
                                  href={deploymentData.deployment.liveUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-500 hover:bg-green-500/20 transition-colors"
                                  data-testid="link-live-url"
                                >
                                  <ExternalLink className="w-5 h-5" />
                                  <span className="font-medium">View Live Site</span>
                                </a>
                              )}

                              {deploymentData.deployment.repoUrl && (
                                <a 
                                  href={deploymentData.deployment.repoUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-3 bg-background/50 border border-white/5 rounded-lg hover:bg-muted transition-colors"
                                  data-testid="link-repo-url"
                                >
                                  <Github className="w-5 h-5" />
                                  <span>View Repository</span>
                                </a>
                              )}

                              <div className="space-y-2 mt-4">
                                <h4 className="text-sm font-medium">Pipeline Stages</h4>
                                {deploymentData.stages.map((stage) => (
                                  <div 
                                    key={stage.stageType}
                                    className="flex items-center justify-between p-2 rounded bg-background/50"
                                  >
                                    <div className="flex items-center gap-2">
                                      {stage.status === "pending" && <Clock className="w-4 h-4 text-gray-400" />}
                                      {stage.status === "running" && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                                      {stage.status === "completed" && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                                      {stage.status === "failed" && <Lock className="w-4 h-4 text-red-400" />}
                                      <span className="text-sm capitalize">
                                        {stage.stageType.replace(/_/g, " ")}
                                      </span>
                                    </div>
                                    {stage.error && (
                                      <span className="text-xs text-red-400 max-w-[150px] truncate">
                                        {stage.error}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <Rocket className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                              <p className="text-muted-foreground">
                                {currentOrder.paidStatus !== "paid" 
                                  ? "Deployment will start after payment is confirmed"
                                  : "No deployment in progress"}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </>
        )}
      </MainContent>
    </div>
    </>
  );
}
